/**
 * Recon's one room list, at Home and at /boardroom alike.
 *
 * TWO LISTS OF THE SAME ROOMS. Home showed "Waiting on you", account totals,
 * "Digging now" and "Lately"; Recons showed every room as a card with dig
 * buttons. Neither did the other's job: Home could not start a room, Recons
 * could not say which rooms needed you. Now there is one list: search, sort,
 * and a status word for each room - needs you, digging, idle - with what needs
 * you on top and empty rooms folded into a line underneath.
 *
 * NO DIG BUTTONS HERE. Spending happens inside a room, where you can see what
 * it has and what a dig will chase.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import PaywallNotice from '../components/common/PaywallNotice';
import { relativeAge } from '../components/common/relativeAge';
import { paywallOf } from '../billing/credits';
import api from '../services/api';
import { tally } from './boardroomProgress';
import {
  roomStatus,
  searchRooms,
  sortRooms,
  splitEmpty,
  STATUS_WORD,
  waitingLine,
  type RoomSort,
  type RoomStatus,
} from './roomList';
import type { BoardroomSummary, Paywall } from '../types';

/** One request's worth of rooms, and how many we are willing to make. Past it
 *  the page says what it counted. */
const PAGE = 100;
const MAX_PAGES = 20;

const STATUS_TONE: Record<RoomStatus, string> = {
  needs_you: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  digging: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  idle: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  empty: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const SORTS: { key: RoomSort; label: string }[] = [
  { key: 'status', label: 'Needs you first' },
  { key: 'updated', label: 'Last updated' },
  { key: 'name', label: 'Name' },
];

const SORT_KEY = 'jcseo.recon.roomSort';

function storedSort(): RoomSort {
  try {
    const raw = window.localStorage.getItem(SORT_KEY);
    return raw === 'updated' || raw === 'name' ? raw : 'status';
  } catch {
    return 'status';
  }
}

/** What a row says under the room's name: why it needs you, what a dig has
 *  spent so far, or what the room has found. */
function rowLine(r: BoardroomSummary, status: RoomStatus): string {
  if (status === 'needs_you') return waitingLine(r);
  // Quiet for two weeks: not urgent, but still say what is waiting.
  if (status === 'idle' && waitingLine(r)) return waitingLine(r);
  if (status === 'digging' && r.run) {
    return `digging now · ${(r.run.tokens_billed ?? r.run.tokens_spent).toLocaleString()} tokens so far`;
  }
  return tally(r.item_counts);
}

export default function BoardroomsHome() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<BoardroomSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<Paywall | null>(null);
  const [opening, setOpening] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<RoomSort>(storedSort);
  const [showEmpty, setShowEmpty] = useState(false);

  const load = useCallback(async () => {
    try {
      const first = await api.listBoardrooms({ limit: PAGE });
      const all = [...first.items];
      for (let page = 1; all.length < first.total && page < MAX_PAGES && first.items.length > 0; page++) {
        const next = await api.listBoardrooms({ limit: PAGE, offset: page * PAGE });
        if (next.items.length === 0) break;
        all.push(...next.items);
      }
      setTotal(first.total);
      setRooms(all);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your rooms');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  // No live feed here: while any room digs, read the list again now and then,
  // or a finished dig keeps saying "digging".
  const anyDigging = (rooms ?? []).some(r => r.run?.status === 'RUNNING');
  useEffect(() => {
    if (!anyDigging) return;
    const t = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(t);
  }, [anyDigging, load]);

  const pickSort = (next: RoomSort) => {
    setSort(next);
    try {
      window.localStorage.setItem(SORT_KEY, next);
    } catch {
      /* private mode: per visit */
    }
  };

  // AN EMPTY ROOM, IMMEDIATELY: the room's chat settles what it is about.
  const openRoom = async () => {
    if (opening) return;
    setOpening(true);
    setError(null);
    try {
      const room = await api.createBoardroom({ name: 'New room', description: '' });
      navigate(`/boardroom/${room.id}`);
    } catch (e) {
      const pw = paywallOf(e);
      if (pw) setPaywall(pw);
      else setError(e instanceof Error ? e.message : 'Could not open a room');
      setOpening(false);
    }
  };

  const shown = useMemo(() => splitEmpty(sortRooms(searchRooms(rooms ?? [], q), sort)), [rooms, q, sort]);
  const needsYou = (rooms ?? []).filter(r => roomStatus(r) === 'needs_you').length;

  return (
    <div>
      <PageHeader
        title="Recons"
        subtitle={
          rooms && rooms.length
            ? `${rooms.length} room${rooms.length === 1 ? '' : 's'}${
                needsYou ? ` · ${needsYou} need${needsYou === 1 ? 's' : ''} you` : ''
              }`
            : undefined
        }
        actions={
          <button
            type="button"
            onClick={openRoom}
            disabled={opening}
            className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {opening ? 'Opening…' : 'New recon'}
          </button>
        }
      />

      {paywall && (
        <div className="mb-4">
          <PaywallNotice paywall={paywall} onDismiss={() => setPaywall(null)} />
        </div>
      )}
      {error && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {!rooms ? (
        !error && <p className="text-sm text-gray-400">Loading…</p>
      ) : rooms.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300">You have no rooms yet.</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Name a market, a complaint or a question. The room finds who already sells there and what their customers
            complain about.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Find a room"
              aria-label="Find a room"
              className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:max-w-xs"
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              Sort
              <select
                value={sort}
                onChange={e => pickSort(e.target.value as RoomSort)}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                data-testid="room-sort"
              >
                {SORTS.map(s => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {shown.rooms.length === 0 && shown.empty.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No room matches “{q}”.</p>
          ) : (
            shown.rooms.length > 0 && (
              <ul
                className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800"
                data-testid="room-list"
              >
                {shown.rooms.map(r => {
                  const status = roomStatus(r);
                  return (
                    <li key={r.id}>
                      <Link
                        to={`/boardroom/${r.id}`}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div className="min-w-0 flex-1" data-testid={`room-${r.id}`}>
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{r.name}</span>
                            <span className={`rounded-full px-2 py-px text-[11px] ${STATUS_TONE[status]}`}>
                              {STATUS_WORD[status]}
                            </span>
                          </div>
                          {r.description && (
                            <p className="mt-0.5 line-clamp-1 text-sm text-gray-500 dark:text-gray-400">
                              {r.description}
                            </p>
                          )}
                          <p
                            className={`mt-0.5 text-xs ${
                              status === 'needs_you'
                                ? 'text-amber-700 dark:text-amber-300'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {rowLine(r, status)}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                          {relativeAge(r.updated_at)}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )
          )}

          {/* EMPTY ROOMS FOLD AWAY: a room nobody has set up yet is one line,
              not a card the size of one that found forty companies. */}
          {shown.empty.length > 0 && (
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400" data-testid="empty-rooms">
              <button
                type="button"
                onClick={() => setShowEmpty(s => !s)}
                className="hover:text-gray-800 dark:hover:text-gray-200"
                aria-expanded={showEmpty}
              >
                {showEmpty ? 'Hide' : 'Show'} {shown.empty.length} empty room{shown.empty.length === 1 ? '' : 's'}
              </button>
              {showEmpty && (
                <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {shown.empty.map(r => (
                    <li key={r.id}>
                      <Link to={`/boardroom/${r.id}`} className="text-indigo-600 hover:underline dark:text-indigo-300">
                        {r.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {total > rooms.length && (
            <p className="mt-3 text-xs text-gray-400">
              Showing your {rooms.length} most recent rooms of {total}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
